import React, { useState } from 'react';
import {
  BarChart2,
  BookOpen,
  Clock,
  ChevronDown,
  ChevronUp,
  Star,
  UserCheck,
  Compass,
  Sparkles,
  Target
} from 'lucide-react';
import KickstartBanner from '../../components/KickstartBanner/KickstartBanner';
import './CourseDetailsPage.css';

const PROGRAMS_DATA = {
  "future-pathways": {
    id: 2,
    slug: "future-pathways",
    title: "Future Pathways",
    target: "Designed for: Class 9–10 Students",
    desc: "Future Pathways helps Class 9–10 students understand their strengths, interests and aspirations, explore suitable career directions, and build clarity before making important stream and subject choices.",
    category: "Career Guidance",
    author: "By V. Swaminathan",
    price: "",
    rating: "4.9",
    duration: "Personalised Coaching",
    lessonsCount: "5 Modules",
    level: "Class 9–10",
    image: "/images/Home/Course2.jpeg",
    ctaText: "Book a Free 45-Minute Consultation",
    specs: [
      {
        icon: Target,
        label: "Designed for:",
        value: "Class 9–10 Students"
      },
      {
        icon: Sparkles,
        label: "Focus:",
        value: "Career Exploration & Future Pathways"
      },
      {
        icon: UserCheck,
        label: "Approach:",
        value: "Personalised Career Coaching"
      },
      {
        icon: Compass,
        label: "Goal:",
        value: "Build clarity before Stream Selection"
      }
    ],
    overview: {
      heading: "Program Overview",
      desc: "Future Pathways gives Class 9–10 students structured guidance to understand themselves, explore career possibilities and make informed decisions about their next academic steps. The process combines self-discovery, scientific assessment, guided research and personalised coaching."
    },
    modules: [
      {
        id: 1,
        title: "01. Self-Discovery",
        content: "Understand strengths, interests, personality, motivations, values and aspirations."
      },
      {
        id: 2,
        title: "02. Understanding Possibilities",
        content: "Explore education options, career possibilities, emerging industries and future-ready opportunities."
      },
      {
        id: 3,
        title: "03. Stream & Subject Exploration",
        content: "Explore suitable streams and subjects based on the student's strengths, interests and future possibilities."
      },
      {
        id: 4,
        title: "04. Career Pathway Research",
        content: "Use guided self-research to explore multiple right-fit career pathways and understand what they involve."
      },
      {
        id: 5,
        title: "05. Parent–Student Alignment",
        content: "Help students and parents have clearer, healthier conversations around education and career decisions."
      }
    ],
    sidebarItems: [
      {
        icon: UserCheck,
        title: "Personalised Coaching",
        desc: "Guidance designed around the student's individual strengths, interests, goals and aspirations."
      },
      {
        icon: BarChart2,
        title: "Scientific Assessment",
        desc: "Scientifically validated assessments provide deeper insights that support the career discovery process."
      },
      {
        icon: Compass,
        title: "Guided Career Exploration",
        desc: "Students explore suitable career pathways through structured questioning, research and reflection."
      },
      {
        icon: Sparkles,
        title: "Future-Focused Planning",
        desc: "Explore emerging industries, future-ready skills and career opportunities while planning the next academic step."
      }
    ]
  },
  "beyond-marks": {
    id: 1,
    slug: "beyond-marks",
    title: "Beyond Marks",
    target: "Designed for: Class 7–8 Students",
    desc: "Beyond Marks helps Class 7–8 students discover their strengths, interests, personality and aspirations—building self-awareness and clarity for their future education and career choices.",
    category: "Career Guidance",
    author: "By V. Swaminathan",
    price: "",
    rating: "4.9",
    duration: "Personalised Coaching",
    lessonsCount: "5 Modules",
    level: "Class 7–8",
    image: "/images/course_banner_detail.jpg",
    ctaText: "Book a Free 45-Minute Consultation",
    specs: [
      {
        icon: Target,
        label: "Designed for:",
        value: "Class 7–8 Students"
      },
      {
        icon: Sparkles,
        label: "Focus:",
        value: "Self-Discovery & Strength Discovery"
      },
      {
        icon: UserCheck,
        label: "Approach:",
        value: "Personalised Career Coaching"
      },
      {
        icon: Compass,
        label: "Goal:",
        value: "Understand strengths, interests & possibilities"
      }
    ],
    overview: {
      heading: "Program overview",
      desc: "Beyond Marks helps Class 7–8 students discover their strengths, interests, personality and aspirations—building self-awareness and clarity for their future education and career choices."
    },
    modules: [
      {
        id: 1,
        title: "01.  Self-Discovery",
        content: "Help students understand their strengths, interests, personality, motivations and aspirations."
      },
      {
        id: 2,
        title: "02.  Understanding Possibilities",
        content: "Explore different possibilities instead of limiting decisions only to academic marks."
      },
      {
        id: 3,
        title: "03.  Career Exploration",
        content: "Encourage students to explore suitable future pathways through guided discovery."
      },
      {
        id: 4,
        title: "04. Building Clarity",
        content: "Help students develop greater self-awareness and confidence around their future."
      },
      {
        id: 5,
        title: "05. Parent–Student Understanding",
        content: "Create better understanding around the student's individual strengths and direction."
      }
    ],
    sidebarItems: [
      {
        icon: UserCheck,
        title: "Personalised Coaching",
        desc: "Guidance designed around the student's individual strengths, interests and aspirations."
      },
      {
        icon: BarChart2,
        title: "Scientific Assessment",
        desc: "Use scientifically validated assessments as part of the broader career discovery process."
      },
      {
        icon: Compass,
        title: "Guided Self-Discovery",
        desc: "Students are encouraged to understand themselves and take ownership of their education and career decisions."
      },
      {
        icon: Sparkles,
        title: "Future-Focused Exploration",
        desc: "Explore future-ready skills, emerging industries and career possibilities in an age-appropriate way."
      }
    ]
  },
  "career-discovery": {
    id: 4,
    slug: "career-discovery",
    title: "Career Discovery",
    target: "Designed for: Class 11–12 Students",
    desc: "Career Discovery helps Class 11–12 students understand themselves, explore right-fit career pathways and make confident decisions about their future. Through structured self-discovery, scientific assessment and guided career research, students build clarity before choosing their next step.",
    category: "Career Guidance",
    author: "By V. Swaminathan",
    price: "",
    rating: "4.9",
    duration: "Personalised Coaching",
    lessonsCount: "5 Modules",
    level: "Class 11–12",
    image: "/images/Home/Course3.jpeg",
    ctaText: "Book a Free 45-Minute Consultation",
    specs: [
      {
        icon: Target,
        label: "Designed for:",
        value: "Class 11–12 Students"
      },
      {
        icon: Sparkles,
        label: "Focus:",
        value: "Career Discovery & Stream Selection"
      },
      {
        icon: UserCheck,
        label: "Approach:",
        value: "Personalised Career Coaching"
      },
      {
        icon: Compass,
        label: "Goal:",
        value: "Build Clarity for the Right-Fit Career"
      }
    ],
    overview: {
      heading: "Program Overview",
      desc: "Career Discovery gives Class 11–12 students a structured way to understand their strengths, interests, personality, motivations and aspirations before making important education and career decisions. Students explore suitable pathways through assessment, guided research and personalised coaching."
    },
    modules: [
      {
        id: 1,
        title: "01. Self-Discovery",
        content: "Understand strengths, interests, personality, motivations, values and aspirations to build a stronger foundation for career decisions."
      },
      {
        id: 2,
        title: "02. Psychometric Assessment",
        content: "Use scientifically validated assessments to gain deeper insights into aptitude, personality, interests and other factors influencing career choices."
      },
      {
        id: 3,
        title: "03. Career Exploration",
        content: "Explore multiple career pathways through guided research and understand how different options connect with the student's potential."
      },
      {
        id: 4,
        title: "04. Stream & Subject Alignment",
        content: "Review education choices and identify suitable stream and subject directions based on the student's emerging career interests."
      },
      {
        id: 5,
        title: "05. Career Action Plan",
        content: "Validate the chosen direction and create practical next steps for education, career preparation and future planning."
      }
    ],
    sidebarItems: [
      {
        icon: UserCheck,
        title: "Personalised Coaching",
        desc: "One-to-one guidance built around the student's strengths, interests, aspirations and career goals."
      },
      {
        icon: BarChart2,
        title: "Scientific Assessment",
        desc: "Scientifically validated assessments provide deeper insights to support informed career decisions."
      },
      {
        icon: Compass,
        title: "Guided Career Research",
        desc: "Students explore career pathways through structured research rather than relying only on recommendations."
      },
      {
        icon: Sparkles,
        title: "Strategic Career Planning",
        desc: "Connect career direction with stream, subjects, higher education options and practical next steps."
      }
    ]
  },
  "career-launch": {
    id: 5,
    slug: "career-launch",
    title: "Career Launch",
    target: "Designed for: Undergraduate Students",
    desc: "Career Launch helps undergraduate students understand their strengths, interests and career goals, explore suitable career pathways and prepare for a confident transition from college to the world of work.",
    category: "Higher Education",
    author: "By V. Swaminathan",
    price: "",
    rating: "4.9",
    duration: "Personalised Coaching",
    lessonsCount: "5 Modules",
    level: "Undergraduate",
    image: "/images/Home/Course4.jpeg",
    ctaText: "Book a Free 45-Minute Consultation",
    specs: [
      {
        icon: Target,
        label: "Designed for:",
        value: "Undergraduate Students"
      },
      {
        icon: Sparkles,
        label: "Focus:",
        value: "Career Direction & Career Readiness"
      },
      {
        icon: UserCheck,
        label: "Approach:",
        value: "Personalised Career Coaching"
      },
      {
        icon: Compass,
        label: "Goal:",
        value: "Build Clarity and Confidence for Career Launch"
      }
    ],
    overview: {
      heading: "Program Overview",
      desc: "Career Launch provides undergraduate students with structured guidance to understand their potential, clarify their career direction and explore pathways that align with their strengths, interests and aspirations. The program helps students move from uncertainty to informed career decisions and purposeful action."
    },
    modules: [
      {
        id: 1,
        title: "01. Self-Discovery",
        content: "Understand strengths, interests, personality, motivations, values and aspirations to build a strong foundation for career decisions."
      },
      {
        id: 2,
        title: "02. Career Exploration",
        content: "Explore suitable career pathways, industries and roles through structured research and guided discovery."
      },
      {
        id: 3,
        title: "03. Career Direction",
        content: "Connect the student's strengths and interests with realistic career possibilities and identify potential right-fit pathways."
      },
      {
        id: 4,
        title: "04. Career Readiness",
        content: "Identify the skills, experiences and practical preparation needed to move confidently towards the chosen career direction."
      },
      {
        id: 5,
        title: "05. Action & Career Planning",
        content: "Create a practical roadmap with clear next steps for skill development, experience building and career progression."
      }
    ],
    sidebarItems: [
      {
        icon: UserCheck,
        title: "Personalised Coaching",
        desc: "Guidance designed around the student's strengths, interests, aspirations and evolving career goals."
      },
      {
        icon: BarChart2,
        title: "Scientific Assessment",
        desc: "Scientifically validated assessments provide deeper insights into factors that influence career decisions."
      },
      {
        icon: Compass,
        title: "Career Pathway Exploration",
        desc: "Explore multiple career possibilities and understand how different pathways connect with the student's potential."
      },
      {
        icon: Sparkles,
        title: "Strategic Career Planning",
        desc: "Turn career clarity into practical next steps for skills, experience and future opportunities."
      }
    ]
  },
  "global-pathways-postgrad": {
    id: 6,
    slug: "global-pathways-postgrad",
    title: "Global Pathways",
    target: "Designed for: Undergraduate Students Planning a Master’s Abroad",
    desc: "Global Pathways helps undergraduate students understand their strengths and career goals, explore suitable Master’s pathways abroad, and make informed decisions about higher education and their future.",
    category: "Higher Education",
    author: "By V. Swaminathan",
    price: "",
    rating: "4.9",
    duration: "Personalised Coaching",
    lessonsCount: "5 Modules",
    level: "Master’s Abroad",
    image: "/images/Home/Course6.jpeg",
    ctaText: "Book a Free 45-Minute Consultation",
    specs: [
      {
        icon: Target,
        label: "Designed for:",
        value: "Undergraduate Students Planning a Master’s Abroad"
      },
      {
        icon: Sparkles,
        label: "Focus:",
        value: "Global Higher-Education & Career Pathway Planning"
      },
      {
        icon: UserCheck,
        label: "Approach:",
        value: "Personalised Career Coaching & Strategic Guidance"
      },
      {
        icon: Compass,
        label: "Goal:",
        value: "Identify the Right Master’s Pathway with Clarity"
      }
    ],
    overview: {
      heading: "Program Overview",
      desc: "Global Pathways provides structured guidance for undergraduate students planning postgraduate study abroad. Students explore their interests and career direction, research suitable courses and institutions, validate their choices, and create a practical plan for their next academic step."
    },
    modules: [
      {
        id: 1,
        title: "01. Self-Discovery",
        content: "Understand strengths, interests, personality, motivations and career aspirations to establish a clear foundation for postgraduate decisions."
      },
      {
        id: 2,
        title: "02. Career & Course Exploration",
        content: "Explore career directions, Master’s specialisations and study pathways that align with the student's goals and potential."
      },
      {
        id: 3,
        title: "03. Global University & Pathway Research",
        content: "Research suitable countries, universities, programmes and higher-education pathways based on the student's academic and career direction."
      },
      {
        id: 4,
        title: "04. Pathway Validation",
        content: "Review and validate shortlisted study options to ensure they align with the student's long-term career goals and aspirations."
      },
      {
        id: 5,
        title: "05. Strategic Action Plan",
        content: "Build a practical roadmap covering the next steps towards postgraduate study abroad and future career development."
      }
    ],
    sidebarItems: [
      {
        icon: UserCheck,
        title: "Personalised Coaching",
        desc: "Guidance tailored to the student's strengths, interests, aspirations and postgraduate goals."
      },
      {
        icon: BarChart2,
        title: "Psychometric Assessment",
        desc: "Use scientifically validated assessments to gain deeper insights into personality, interests and career direction."
      },
      {
        icon: Compass,
        title: "Global Pathway Exploration",
        desc: "Explore suitable international higher-education and career pathways based on the student's goals."
      },
      {
        icon: Sparkles,
        title: "Strategic Planning",
        desc: "Turn validated choices into a clear action plan for the student's postgraduate journey."
      }
    ]
  },
  "global-pathways-undergrad": {
    id: 3,
    slug: "global-pathways-undergrad",
    title: "Global Pathways",
    target: "Designed for: Class 9–11 Students Planning a Bachelor's Degree Abroad",
    desc: "Global Pathways helps students explore undergraduate study options abroad with greater clarity. It connects self-discovery, career direction and future goals with the right higher education pathways, helping students make informed decisions about countries, courses and universities.",
    category: "Career Guidance",
    author: "By V. Swaminathan",
    price: "",
    rating: "4.9",
    duration: "Personalised Coaching",
    lessonsCount: "5 Modules",
    level: "Class 9–11",
    image: "/images/Home/Course3.jpeg",
    ctaText: "Book a Free 45-Minute Consultation",
    specs: [
      {
        icon: Target,
        label: "Designed for:",
        value: "Class 9–11 Students"
      },
      {
        icon: Sparkles,
        label: "Focus:",
        value: "Global Higher Education & Career Pathways"
      },
      {
        icon: UserCheck,
        label: "Approach:",
        value: "Personalised Higher Education Coaching"
      },
      {
        icon: Compass,
        label: "Goal:",
        value: "Build Clarity for Undergraduate Study Abroad"
      }
    ],
    overview: {
      heading: "Program Overview",
      desc: "Global Pathways helps students understand how their career interests and educational goals connect with undergraduate opportunities abroad. Through personalised guidance, career research and strategic planning, students can explore suitable pathways and make more informed higher education decisions."
    },
    modules: [
      {
        id: 1,
        title: "01. Self-Discovery",
        content: "Understand strengths, interests, personality, motivations, values and aspirations as the foundation for future education and career decisions."
      },
      {
        id: 2,
        title: "02. Career & Pathway Exploration",
        content: "Explore career possibilities and identify education pathways that align with the student's strengths, interests and aspirations."
      },
      {
        id: 3,
        title: "03. Global Education Exploration",
        content: "Explore undergraduate study opportunities abroad and understand how different education pathways connect with future goals."
      },
      {
        id: 4,
        title: "04. Course & University Planning",
        content: "Research suitable courses, universities and higher education options based on the student's emerging career direction."
      },
      {
        id: 5,
        title: "05. Strategic Action Planning",
        content: "Build a practical roadmap for the next academic steps, supporting informed and confident higher education decisions."
      }
    ],
    sidebarItems: [
      {
        icon: UserCheck,
        title: "Personalised Coaching",
        desc: "Guidance tailored to the student's individual strengths, interests, aspirations and higher education goals."
      },
      {
        icon: BarChart2,
        title: "Scientific Assessment",
        desc: "Scientifically validated assessments provide insights that support deeper self-understanding and career exploration."
      },
      {
        icon: Compass,
        title: "Global Education Planning",
        desc: "Explore undergraduate education pathways abroad and understand how they connect with future career possibilities."
      },
      {
        icon: Sparkles,
        title: "Strategic Pathway Planning",
        desc: "Connect career direction with courses, universities and practical next steps for undergraduate study abroad."
      }
    ]
  },
  "global-pathways": {
    id: 6,
    slug: "global-pathways",
    title: "Global Pathways",
    target: "Designed for: Undergraduate Students Planning a Master’s Abroad",
    desc: "Global Pathways helps undergraduate students understand their strengths and career goals, explore suitable Master’s pathways abroad, and make informed decisions about higher education and their future.",
    category: "Higher Education",
    author: "By V. Swaminathan",
    price: "",
    rating: "4.9",
    duration: "Personalised Coaching",
    lessonsCount: "5 Modules",
    level: "Master’s Abroad",
    image: "/images/Home/Course6.jpeg",
    ctaText: "Book a Free 45-Minute Consultation",
    specs: [
      {
        icon: Target,
        label: "Designed for:",
        value: "Undergraduate Students Planning a Master’s Abroad"
      },
      {
        icon: Sparkles,
        label: "Focus:",
        value: "Global Higher-Education & Career Pathway Planning"
      },
      {
        icon: UserCheck,
        label: "Approach:",
        value: "Personalised Career Coaching & Strategic Guidance"
      },
      {
        icon: Compass,
        label: "Goal:",
        value: "Identify the Right Master’s Pathway with Clarity"
      }
    ],
    overview: {
      heading: "Program Overview",
      desc: "Global Pathways provides structured guidance for undergraduate students planning postgraduate study abroad. Students explore their interests and career direction, research suitable courses and institutions, validate their choices, and create a practical plan for their next academic step."
    },
    modules: [
      {
        id: 1,
        title: "01. Self-Discovery",
        content: "Understand strengths, interests, personality, motivations and career aspirations to establish a clear foundation for postgraduate decisions."
      },
      {
        id: 2,
        title: "02. Career & Course Exploration",
        content: "Explore career directions, Master’s specialisations and study pathways that align with the student's goals and potential."
      },
      {
        id: 3,
        title: "03. Global University & Pathway Research",
        content: "Research suitable countries, universities, programmes and higher-education pathways based on the student's academic and career direction."
      },
      {
        id: 4,
        title: "04. Pathway Validation",
        content: "Review and validate shortlisted study options to ensure they align with the student's long-term career goals and aspirations."
      },
      {
        id: 5,
        title: "05. Strategic Action Plan",
        content: "Build a practical roadmap covering the next steps towards postgraduate study abroad and future career development."
      }
    ],
    sidebarItems: [
      {
        icon: UserCheck,
        title: "Personalised Coaching",
        desc: "Guidance tailored to the student's strengths, interests, aspirations and postgraduate goals."
      },
      {
        icon: BarChart2,
        title: "Psychometric Assessment",
        desc: "Use scientifically validated assessments to gain deeper insights into personality, interests and career direction."
      },
      {
        icon: Compass,
        title: "Global Pathway Exploration",
        desc: "Explore suitable international higher-education and career pathways based on the student's goals."
      },
      {
        icon: Sparkles,
        title: "Strategic Planning",
        desc: "Turn validated choices into a clear action plan for the student's postgraduate journey."
      }
    ]
  },
  "career-acceleration": {
    id: 7,
    slug: "career-acceleration",
    title: "Career Acceleration",
    target: "Designed for: Postgraduate Students",
    desc: "Career Acceleration helps postgraduate students gain clarity on their career direction, understand their strengths and interests, and make informed decisions about their next professional step.",
    category: "Higher Education",
    author: "By V. Swaminathan",
    price: "",
    rating: "4.9",
    duration: "Personalised Coaching",
    lessonsCount: "5 Modules",
    level: "Postgraduate",
    image: "/images/Home/Course5.jpeg",
    ctaText: "Book a Free 45-Minute Consultation",
    specs: [
      {
        icon: Target,
        label: "Designed for:",
        value: "Postgraduate Students"
      },
      {
        icon: Sparkles,
        label: "Focus:",
        value: "Career Direction & Professional Growth"
      },
      {
        icon: UserCheck,
        label: "Approach:",
        value: "Personalised Career Coaching & Assessment"
      },
      {
        icon: Compass,
        label: "Goal:",
        value: "Build Clarity for the Next Career Step"
      }
    ],
    overview: {
      heading: "Program Overview",
      desc: "Career Acceleration provides personalised guidance for postgraduate students navigating important career decisions. The programme helps students understand themselves, explore suitable career possibilities, evaluate their options and create a clearer direction for their professional journey."
    },
    modules: [
      {
        id: 1,
        title: "01. Self-Discovery",
        content: "Understand strengths, interests, personality, motivations and aspirations to build a strong foundation for career decisions."
      },
      {
        id: 2,
        title: "02. Career Direction",
        content: "Identify career areas and professional possibilities that align with the student's strengths, interests and long-term aspirations."
      },
      {
        id: 3,
        title: "03. Career Exploration",
        content: "Explore relevant career roles, industries and opportunities to understand the possibilities available after postgraduate study."
      },
      {
        id: 4,
        title: "04. Building Career Clarity",
        content: "Connect personal strengths and career interests with realistic professional pathways and make more confident decisions."
      },
      {
        id: 5,
        title: "05. Career Action Planning",
        content: "Develop a practical roadmap to move forward with greater clarity, confidence and purpose."
      }
    ],
    sidebarItems: [
      {
        icon: UserCheck,
        title: "Personalised Coaching",
        desc: "Guidance tailored to the student's individual strengths, interests, aspirations and career goals."
      },
      {
        icon: BarChart2,
        title: "Scientific Assessment",
        desc: "Use scientifically validated assessments to gain deeper insight into personality, interests and career direction."
      },
      {
        icon: Compass,
        title: "Career Exploration",
        desc: "Explore suitable career roles, industries and professional possibilities based on the student's profile."
      },
      {
        icon: Sparkles,
        title: "Practical Career Planning",
        desc: "Turn career insights into a clear and actionable direction for the next stage of professional growth."
      }
    ]
  },
  "career-transition": {
    id: 8,
    slug: "career-transition",
    title: "Career Transition",
    target: "Designed for: Young Working Professionals",
    desc: "Career Transition helps young working professionals gain clarity about their strengths, interests and career goals while exploring suitable directions for their next professional chapter.",
    category: "Working Professionals",
    author: "By V. Swaminathan",
    price: "",
    rating: "4.9",
    duration: "Personalised Coaching",
    lessonsCount: "5 Modules",
    level: "Working Professionals",
    image: "/images/Home/Course6.jpeg",
    ctaText: "Book a Free 45-Minute Consultation",
    specs: [
      {
        icon: Target,
        label: "Designed for:",
        value: "Young Working Professionals"
      },
      {
        icon: Sparkles,
        label: "Focus:",
        value: "Career Direction & Transition Planning"
      },
      {
        icon: UserCheck,
        label: "Approach:",
        value: "Personalised Career Coaching & Assessment"
      },
      {
        icon: Compass,
        label: "Goal:",
        value: "Build Clarity and Confidence for the Next Career Step"
      }
    ],
    overview: {
      heading: "Program Overview",
      desc: "Career Transition provides structured guidance for young professionals who are considering a change in career direction. Through self-discovery, assessment, career research and guided reflection, the programme helps individuals understand their options and create a practical path forward."
    },
    modules: [
      {
        id: 1,
        title: "01. Self-Discovery",
        content: "Understand your strengths, interests, personality, motivations and aspirations to identify what matters in your career."
      },
      {
        id: 2,
        title: "02. Career Direction",
        content: "Reflect on your current experience, interests and aspirations to explore possible directions for your next career step."
      },
      {
        id: 3,
        title: "03. Career Exploration",
        content: "Research suitable career paths and future opportunities that connect with your strengths and professional goals."
      },
      {
        id: 4,
        title: "04. Building Career Clarity",
        content: "Evaluate your options through guided reflection and validation to make more informed career decisions."
      },
      {
        id: 5,
        title: "05. Action Planning",
        content: "Create a practical roadmap for moving towards your chosen career direction with greater confidence and ownership."
      }
    ],
    sidebarItems: [
      {
        icon: UserCheck,
        title: "Personalised Coaching",
        desc: "One-to-one guidance built around your strengths, interests, aspirations and career goals."
      },
      {
        icon: BarChart2,
        title: "Psychometric Assessment",
        desc: "Scientifically validated assessments can provide deeper insights into personality, interests and factors influencing career decisions."
      },
      {
        icon: Compass,
        title: "Career Research",
        desc: "Explore career possibilities and future opportunities through structured research and guided discovery."
      },
      {
        icon: Sparkles,
        title: "Strategic Action Planning",
        desc: "Turn your career insights and validated choices into a practical plan for your next step."
      }
    ]
  },
  "career-revival": {
    id: 9,
    slug: "career-revival",
    title: "Career Revival",
    target: "Designed for: Unemployed Graduates",
    desc: "Career Revival helps unemployed graduates gain clarity about their strengths, interests and career direction while exploring suitable pathways for their next professional opportunity.",
    category: "Working Professionals",
    author: "By V. Swaminathan",
    price: "",
    rating: "4.9",
    duration: "Personalised Coaching",
    lessonsCount: "5 Modules",
    level: "Unemployed Graduates",
    image: "/images/Home/Course2.jpeg",
    ctaText: "Book a Free 45-Minute Consultation",
    specs: [
      {
        icon: Target,
        label: "Designed for:",
        value: "Unemployed Graduates"
      },
      {
        icon: Sparkles,
        label: "Focus:",
        value: "Career Clarity & Direction"
      },
      {
        icon: UserCheck,
        label: "Approach:",
        value: "Personalised Career Coaching & Assessment"
      },
      {
        icon: Compass,
        label: "Goal:",
        value: "Discover suitable career pathways and build a practical action plan"
      }
    ],
    overview: {
      heading: "Program overview",
      desc: "Career Revival provides structured guidance for graduates who are currently between opportunities and seeking greater clarity about their career direction. Through self-discovery, assessment, career research and guided reflection, the programme helps individuals understand their options and move forward with confidence."
    },
    modules: [
      {
        id: 1,
        title: "01. Self-Discovery",
        content: "Understand your strengths, interests, personality, motivations and aspirations to build a clearer picture of your career potential."
      },
      {
        id: 2,
        title: "02. Understanding Possibilities",
        content: "Explore different career pathways and opportunities that may align with your strengths, interests and aspirations."
      },
      {
        id: 3,
        title: "03. Career Exploration",
        content: "Research suitable career directions, emerging opportunities and possible pathways through guided career research."
      },
      {
        id: 4,
        title: "04. Building Career Clarity",
        content: "Evaluate your options through reflection, assessment and validation to develop greater confidence in your career direction."
      },
      {
        id: 5,
        title: "05. Action Planning",
        content: "Create a practical roadmap that turns your career insights into clear next steps and supports greater ownership of your career decisions."
      }
    ],
    sidebarItems: [
      {
        icon: UserCheck,
        title: "Personalised Coaching",
        desc: "Guidance designed around your individual strengths, interests, aspirations and career concerns."
      },
      {
        icon: BarChart2,
        title: "Scientific Assessment",
        desc: "Scientifically validated assessments provide insights into factors such as aptitude, personality and interests that can influence career decisions."
      },
      {
        icon: Compass,
        title: "Guided Career Research",
        desc: "Explore suitable career pathways and opportunities through structured research and guided self-discovery."
      },
      {
        icon: Sparkles,
        title: "Career Validation",
        desc: "Reflect on and evaluate potential directions before moving forward with greater clarity and confidence."
      },
      {
        icon: Target,
        title: "Action-Focused Planning",
        desc: "Turn your discoveries into a practical career roadmap with clear and purposeful next steps."
      }
    ]
  },
  "career-restart": {
    id: 10,
    slug: "career-restart",
    title: "Career Restart",
    target: "Designed for: Unemployed Non-Graduates",
    desc: "Career Restart helps unemployed non-graduates understand their strengths, interests and possibilities while exploring practical career directions and building a clear path toward their next opportunity.",
    category: "Working Professionals",
    author: "By V. Swaminathan",
    price: "",
    rating: "4.9",
    duration: "Personalised Coaching",
    lessonsCount: "5 Modules",
    level: "Unemployed Non-Graduates",
    image: "/images/Home/Course1.jpeg",
    ctaText: "Book a Free 45-Minute Consultation",
    specs: [
      {
        icon: Target,
        label: "Designed for:",
        value: "Unemployed Non-Graduates"
      },
      {
        icon: Sparkles,
        label: "Focus:",
        value: "Career Clarity & Direction"
      },
      {
        icon: UserCheck,
        label: "Approach:",
        value: "Personalised Career Coaching & Assessment"
      },
      {
        icon: Compass,
        label: "Goal:",
        value: "Discover suitable career pathways and build a practical action plan"
      }
    ],
    overview: {
      heading: "Program overview",
      desc: "Career Restart provides structured guidance for individuals who are currently unemployed and looking to rebuild their career direction. Through self-discovery, assessment, career research and guided reflection, the programme helps them understand their potential, explore suitable possibilities and move forward with greater confidence."
    },
    modules: [
      {
        id: 1,
        title: "01. Self-Discovery",
        content: "Understand your strengths, interests, personality, motivations and aspirations to build greater awareness of your potential."
      },
      {
        id: 2,
        title: "02. Understanding Possibilities",
        content: "Explore different career possibilities that may align with your strengths, interests and aspirations."
      },
      {
        id: 3,
        title: "03. Career Exploration",
        content: "Research suitable career pathways, opportunities and future-ready options through guided career research."
      },
      {
        id: 4,
        title: "04. Building Career Clarity",
        content: "Reflect on your options, validate potential directions and develop greater confidence in the path you choose."
      },
      {
        id: 5,
        title: "05. Action Planning",
        content: "Turn your career insights into a practical roadmap with clear next steps for moving forward."
      }
    ],
    sidebarItems: [
      {
        icon: UserCheck,
        title: "Personalised Coaching",
        desc: "Guidance designed around your individual strengths, interests, aspirations and current career situation."
      },
      {
        icon: BarChart2,
        title: "Scientific Assessment",
        desc: "Use scientifically validated assessments to gain deeper insights into aptitude, personality, interests and other factors that influence career decisions."
      },
      {
        icon: Compass,
        title: "Guided Career Research",
        desc: "Explore suitable career pathways and opportunities through structured research and guided self-discovery."
      },
      {
        icon: Sparkles,
        title: "Career Validation",
        desc: "Evaluate potential directions through reflection and validation before making important career decisions."
      },
      {
        icon: Target,
        title: "Action-Focused Planning",
        desc: "Build a practical action plan that turns career clarity into meaningful next steps."
      }
    ]
  }
};

const getProgramKey = (c) => {
  if (!c) return 'beyond-marks';
  const title = (c.title || '').toLowerCase().trim();
  const target = (c.target || '').toLowerCase().trim();
  const level = (c.level || '').toLowerCase().trim();

  // Differentiate Global Pathways editions
  if (title.includes('global pathway') || c.slug?.includes('global-pathways')) {
    if (c.slug === 'global-pathways-undergrad' || target.includes('class 9') || target.includes('bachelor') || title.includes('undergrad') || level.includes('class 9')) {
      return 'global-pathways-undergrad';
    }
    return 'global-pathways-postgrad';
  }

  if (title.includes('restart') || c.slug === 'career-restart' || c.id === 10 || target.includes('non-graduate') || level.includes('non-graduate')) return 'career-restart';
  if (title.includes('revival') || c.slug === 'career-revival' || c.id === 9) return 'career-revival';
  if (title.includes('transition') || c.slug === 'career-transition' || c.id === 8) return 'career-transition';
  if (title.includes('acceleration') || c.slug === 'career-acceleration' || c.id === 7) return 'career-acceleration';
  if (title.includes('launch') || c.slug === 'career-launch' || c.id === 5) return 'career-launch';
  if (title.includes('discovery') || c.slug === 'career-discovery' || c.id === 4) return 'career-discovery';
  if (title.includes('future pathway') || c.slug === 'future-pathways' || c.id === 2) return 'future-pathways';
  if (title.includes('beyond mark') || c.slug === 'beyond-marks' || c.id === 1) return 'beyond-marks';
  
  if (c.id === 10) return 'career-restart';
  if (c.id === 9) return 'career-revival';
  if (c.id === 6) return 'global-pathways-postgrad';
  if (c.id === 3) return 'global-pathways-undergrad';
  
  return 'career-restart';
};

function CourseDetailsPage({ course, onSelectCourse, onNavigateToContact }) {
  // Accordion open/close state
  const [openLessonIndex, setOpenLessonIndex] = useState(0);

  const programKey = getProgramKey(course);
  const activeProgram = PROGRAMS_DATA[programKey] || PROGRAMS_DATA['career-restart'];

  const courseData = {
    ...activeProgram,
    ...(course || {}),
    specs: activeProgram.specs,
    overview: activeProgram.overview,
    modules: activeProgram.modules,
    sidebarItems: activeProgram.sidebarItems,
    image: course?.image || activeProgram.image
  };

  const allRelatedCourses = [
    {
      id: 1,
      title: "Beyond Marks",
      author: "By V. Swaminathan",
      target: "Class 7–8",
      rating: "4.9",
      duration: "Personalised Coaching",
      lessons: "5 Modules",
      level: "Class 7–8",
      image: "/images/course_banner_detail.jpg"
    },
    {
      id: 2,
      title: "Future Pathways",
      author: "By V. Swaminathan",
      target: "Class 9–10",
      rating: "4.9",
      duration: "Personalised Coaching",
      lessons: "5 Modules",
      level: "Class 9–10",
      image: "/images/Home/Course2.jpeg"
    },
    {
      id: 3,
      title: "Global Pathways",
      author: "By V. Swaminathan",
      target: "Class 9–11",
      rating: "4.9",
      duration: "Personalised Coaching",
      lessons: "5 Modules",
      level: "Class 9–11",
      image: "/images/Home/Course3.jpeg"
    },
    {
      id: 4,
      title: "Career Discovery",
      author: "By V. Swaminathan",
      target: "Class 11–12",
      rating: "4.9",
      duration: "Personalised Coaching",
      lessons: "5 Modules",
      level: "Class 11–12",
      image: "/images/Home/Course3.jpeg"
    },
    {
      id: 5,
      title: "Career Launch",
      author: "By V. Swaminathan",
      target: "Undergraduate",
      rating: "4.9",
      duration: "Personalised Coaching",
      lessons: "5 Modules",
      level: "Undergraduate",
      image: "/images/Home/Course4.jpeg"
    },
    {
      id: 6,
      title: "Global Pathways",
      author: "By V. Swaminathan",
      target: "Master’s Abroad",
      rating: "4.9",
      duration: "Personalised Coaching",
      lessons: "5 Modules",
      level: "Master’s Abroad",
      image: "/images/Home/Course6.jpeg"
    },
    {
      id: 7,
      title: "Career Acceleration",
      author: "By V. Swaminathan",
      target: "Postgraduate Students",
      rating: "4.9",
      duration: "Personalised Coaching",
      lessons: "5 Modules",
      level: "Postgraduate",
      image: "/images/Home/Course5.jpeg"
    },
    {
      id: 8,
      title: "Career Transition",
      author: "By V. Swaminathan",
      target: "Young Working Professionals",
      rating: "4.9",
      duration: "Personalised Coaching",
      lessons: "5 Modules",
      level: "Working Professionals",
      image: "/images/Home/Course6.jpeg"
    },
    {
      id: 9,
      title: "Career Revival",
      author: "By V. Swaminathan",
      target: "Unemployed Graduates",
      rating: "4.9",
      duration: "Personalised Coaching",
      lessons: "5 Modules",
      level: "Unemployed Graduates",
      image: "/images/Home/Course2.jpeg"
    },
    {
      id: 10,
      title: "Career Restart",
      author: "By V. Swaminathan",
      target: "Unemployed Non-Graduates",
      rating: "4.9",
      duration: "Personalised Coaching",
      lessons: "5 Modules",
      level: "Unemployed Non-Graduates",
      image: "/images/Home/Course1.jpeg"
    }
  ];

  const relatedCourses = allRelatedCourses
    .filter((rel) => rel.title.toLowerCase() !== courseData.title.toLowerCase())
    .slice(0, 3);

  const toggleLesson = (index) => {
    setOpenLessonIndex(openLessonIndex === index ? null : index);
  };

  return (
    <div className="course-details-page">
      <div className="container">

        {/* ================= HERO SPECIFICATION CONTAINER ================= */}
        <div className="course-hero-spec-card">
          <div className="course-hero-img-box">
            <img
              src={courseData.image || "/images/Home/Course2.jpeg"}
              alt={courseData.title}
              className="course-hero-main-img"
            />
          </div>

          <div className="course-hero-info-box">
            <h1 className="course-hero-title">{courseData.title}</h1>
            <p className="course-hero-target">{courseData.target}</p>
            <p className="course-hero-desc">
              {courseData.desc}
            </p>

            <div className="course-spec-list">
              {courseData.specs.map((spec, idx) => {
                const IconComponent = spec.icon;
                return (
                  <div key={idx} className="spec-item">
                    <IconComponent size={18} className="spec-icon" />
                    <span className="spec-text">{spec.label} <strong>{spec.value}</strong></span>
                  </div>
                );
              })}
            </div>

            <button onClick={onNavigateToContact} className="btn-get-access-orange">
              {courseData.ctaText || "Book a Free 45-Minute Consultation"}
            </button>
          </div>
        </div>

        {/* ================= MAIN CONTENT & STICKY SIDEBAR ================= */}
        <div className="course-main-content-grid">

          {/* LEFT COLUMN */}
          <div className="course-left-col">

            {/* LESSONS ACCORDION */}
            <div className="lessons-section">

              {/* PROGRAM OVERVIEW */}
              <div className="course-overview-section">
                <h2 className="section-heading">{courseData.overview.heading}</h2>
                <p className="overview-text">
                  {courseData.overview.desc}
                </p>
              </div>

              <h2 className="section-heading">Modules in this program</h2>

              <div className="accordion-list">
                {courseData.modules.map((lesson, idx) => {
                  const isOpen = openLessonIndex === idx;
                  return (
                    <div key={lesson.id} className={`accordion-item ${isOpen ? 'open' : ''}`}>
                      <button
                        className="accordion-header-btn"
                        onClick={() => toggleLesson(idx)}
                      >
                        <span className="accordion-title">{lesson.title}</span>
                        {isOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                      </button>
                      {isOpen && (
                        <div className="accordion-body">
                          <p>{lesson.content}</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* MEET YOUR CAREER COACH */}
            <div className="teacher-section">
              <h2 className="section-heading">Meet Your Career Coach</h2>

              <div className="teacher-card-row">
                <img
                  src="/images/Home/Founder.png"
                  alt="V. Swaminathan"
                  className="teacher-avatar"
                />
                <div className="teacher-details">
                  <h3 className="teacher-name">V. Swaminathan</h3>
                  <p className="teacher-title">Founder, Deepclariti | Internationally Certified Career Coach | 25+ Years in Higher Education</p>
                </div>
              </div>

              <p className="teacher-bio">
                V. Swaminathan brings over 25 years of Indian and international higher-education experience to career coaching. He has personally coached 10,000+ students and trained 350+ career advisors.
              </p>
            </div>

          </div>

          {/* RIGHT COLUMN: SIDEBAR INFO STACK */}
          <div className="course-right-col">
            <div className="course-sidebar-info-stack">
              {courseData.sidebarItems.map((item, idx) => {
                const IconComp = item.icon;
                return (
                  <div key={idx} className="info-stack-item">
                    <div className="info-stack-header">
                      <IconComp size={22} className="info-stack-icon" />
                      <h3 className="info-stack-title">{item.title}</h3>
                    </div>
                    <p className="info-stack-desc">
                      {item.desc}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>

        </div>

        {/* ================= RELATED COURSES SECTION ================= */}
        <div className="related-courses-section">
          <h2 className="related-courses-title">
            Related <span className="title-highlight-italic">Courses</span>
          </h2>

          <div className="related-courses-grid">
            {relatedCourses.map((relCourse) => (
              <div key={relCourse.id} className="course-card">
                <div className="course-img-wrapper">
                  <img src={relCourse.image} alt={relCourse.title} className="course-img" />
                </div>

                <div className="course-meta-top">
                  <div className="rating-box">
                    <div className="stars-group">
                      <Star size={13} fill="#F59E0B" stroke="none" />
                      <Star size={13} fill="#F59E0B" stroke="none" />
                      <Star size={13} fill="#F59E0B" stroke="none" />
                      <Star size={13} fill="#F59E0B" stroke="none" />
                      <Star size={13} fill="#F59E0B" stroke="none" />
                    </div>
                    <span className="rating-score">({relCourse.rating})</span>
                  </div>
                </div>

                <h3 className="course-card-title">{relCourse.title}</h3>
                <p className="course-author">{relCourse.author}</p>

                <div className="course-details-row">
                  <div className="course-detail-item">
                    <Clock size={15} />
                    <span>{relCourse.duration}</span>
                  </div>
                  <div className="course-detail-item">
                    <BookOpen size={15} />
                    <span>{relCourse.lessons}</span>
                  </div>
                </div>

                <button
                  onClick={() => {
                    onSelectCourse(relCourse);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  className="btn-view-course"
                >
                  View Course
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* ================= KICKSTART BANNER ================= */}
        <KickstartBanner onActionClick={onNavigateToContact} />

      </div>
    </div>
  );
}

export default CourseDetailsPage;
